/*
 * collection-infinite.js
 * -----------------------------------------------------------
 * Masque les produits épuisés dans la grille de collection et
 * comble les trous en chargeant automatiquement les pages
 * suivantes (scroll infini), en conservant filtres & tri.
 *
 * Fonctionne avec le système AJAX du thème (filter-and-sort.js) :
 * à chaque remplacement de [data-ref="main-collection-products"],
 * on réinitialise et on relance le remplissage.
 * -----------------------------------------------------------
 */

(() => {
  const PRODUCTS_SELECTOR = '[data-ref="main-collection-products"]';
  const GRID_SELECTOR = '[data-ref="main-collection-grid"]';
  const CARD_SELECTOR = 'product-card';

  class CollectionInfinite {
    constructor(section) {
      this.section = section;
      this.loading = false;
      this.done = false;
      this.nextUrl = null;
      this.observer = null;
      this.sentinel = null;

      this.init();
    }

    get productsWrap() {
      return this.section.querySelector(PRODUCTS_SELECTOR);
    }

    get grid() {
      return this.section.querySelector(GRID_SELECTOR);
    }

    init() {
      const wrap = this.productsWrap;
      if (!wrap) return;

      // URL de la page suivante fournie par le Liquid
      this.nextUrl = wrap.getAttribute('data-next-url') || null;
      this.done = !this.nextUrl;

      // Masque les épuisés déjà présents
      this.hideSoldOut(this.grid);

      // Masque la pagination native (scroll infini prend le relais)
      const pagination = wrap.querySelector('.pagination-wrapper');
      if (pagination) pagination.style.display = 'none';

      this.setupSentinel();

      // Si la grille est trop courte au départ, on pré-remplit
      this.maybeLoadMore();
    }

    hideSoldOut(scope) {
      if (!scope) return 0;
      let hidden = 0;
      scope.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        const available = card.getAttribute('data-available');
        if (available === 'false') {
          card.remove();
          hidden++;
        }
      });
      return hidden;
    }

    setupSentinel() {
      // Sentinelle en bas de grille pour déclencher le chargement
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'collection-infinite__sentinel';
      this.sentinel.style.cssText = 'width:100%;height:1px;';
      this.productsWrap.appendChild(this.sentinel);

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.loadMore();
          });
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    // Si le viewport n'est pas rempli, on charge encore
    maybeLoadMore() {
      if (this.done || this.loading) return;
      const grid = this.grid;
      if (!grid) return;
      const visibleCards = grid.querySelectorAll(CARD_SELECTOR).length;
      // Seuil : si peu de cartes visibles, on continue de remplir
      if (visibleCards < 8) {
        this.loadMore();
      }
    }

    async loadMore() {
      if (this.loading || this.done || !this.nextUrl) return;
      this.loading = true;

      try {
        const response = await fetch(this.nextUrl);
        if (!response.ok) throw new Error('Fetch page suivante échoué');
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');

        const newWrap = doc.querySelector(PRODUCTS_SELECTOR);
        const newGrid = newWrap ? newWrap.querySelector(GRID_SELECTOR) : null;

        if (!newGrid) {
          this.done = true;
          this.cleanupSentinel();
          return;
        }

        // Retire les épuisés de la nouvelle page
        this.hideSoldOut(newGrid);

        // Injecte les cartes dispos restantes
        const cards = Array.from(newGrid.querySelectorAll(CARD_SELECTOR));
        const grid = this.grid;
        cards.forEach((card) => grid.appendChild(card));

        // Met à jour l'URL de page suivante
        const nextWrap = newWrap.getAttribute('data-next-url');
        this.nextUrl = nextWrap || null;
        this.done = !this.nextUrl;
        if (this.done) this.cleanupSentinel();

        // Si après injection la grille est encore courte, on continue
        this.loading = false;
        this.maybeLoadMore();
        return;
      } catch (err) {
        console.error('[collection-infinite]', err);
        this.done = true;
        this.cleanupSentinel();
      } finally {
        this.loading = false;
      }
    }

    cleanupSentinel() {
      if (this.observer && this.sentinel) {
        this.observer.unobserve(this.sentinel);
      }
      if (this.sentinel) {
        this.sentinel.remove();
        this.sentinel = null;
      }
    }

    destroy() {
      this.cleanupSentinel();
      if (this.observer) this.observer.disconnect();
    }
  }

  // Instance courante par section
  const instances = new WeakMap();

  function mount(section) {
    if (!section) return;
    const existing = instances.get(section);
    if (existing) existing.destroy();
    instances.set(section, new CollectionInfinite(section));
  }

  function mountAll() {
    document.querySelectorAll('.shopify-section-main-collection').forEach(mount);
  }

  // Montage initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  // Remontage après pagination/filtre AJAX du thème
  // filter-and-sort.js émet 'filters:updated' (FiltersUpdatedEvent)
  document.addEventListener('filters:updated', (event) => {
    const section = event.target.closest
      ? event.target.closest('.shopify-section-main-collection')
      : null;
    if (section) {
      mount(section);
    } else {
      mountAll();
    }
  });
})();
